import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CompanyBiometricDeviceUpsertFormFields } from "@/components/companyBiometricDevices/CompanyBiometricDeviceUpsertFormFields";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { syncBiometricDevicesFromApi } from "@/lib/admin/biometricDevicesStore";
import { syncCompaniesFromApi } from "@/lib/admin/companiesStore";
import {
  fetchCompanyBiometricDeviceById,
  getCompanyBiometricDeviceById,
  updateCompanyBiometricDevice,
} from "@/lib/admin/companyBiometricDevicesStore";
import { syncWarehousesFromApi } from "@/lib/catalog/warehousesStore";
import {
  companyBiometricDeviceFormErrorMessage,
  companyBiometricDeviceFormToPayload,
  companyBiometricDeviceToForm,
  type CompanyBiometricDeviceFormState,
} from "@/lib/admin/companyBiometricDeviceForm";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { ArrowLeft, Fingerprint, Loader2, Save } from "lucide-react";
import { motion } from "framer-motion";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

const EditCompanyBiometricDevice = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canUpdate = can(user, "company_biometric_device_can_update");
  const showCompanyPicker = useMemo(
    () => !user?.company_id || can(user, "company_can_view_any"),
    [user],
  );
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [form, setForm] = useState<CompanyBiometricDeviceFormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const record = useMemo(() => (id ? getCompanyBiometricDeviceById(id) : undefined), [id, version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let mounted = true;
    void (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const row = await fetchCompanyBiometricDeviceById(id);
        await Promise.all([
          syncBiometricDevicesFromApi().catch(() => undefined),
          syncWarehousesFromApi({ activeOnly: true }).catch(() => undefined),
          showCompanyPicker ? syncCompaniesFromApi().catch(() => undefined) : Promise.resolve(),
        ]);
        if (mounted) {
          setForm(companyBiometricDeviceToForm(row));
          refresh();
        }
      } catch (e) {
        console.error("[EditCompanyBiometricDevice] Load failed", e);
        if (mounted) {
          if (isAxiosError(e) && e.response?.status === 404) setNotFound(true);
          else toast.error(companyBiometricDeviceFormErrorMessage(e));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, refresh, showCompanyPicker]);

  useEffect(() => {
    if (record && !form) setForm(companyBiometricDeviceToForm(record));
  }, [record, form]);

  const handleCancel = useCallback(() => navigate(id ? `/company-biometric-devices/${id}` : "/company-biometric-devices"), [navigate, id]);

  const save = async () => {
    if (!id || !form || isSaving || !canUpdate) return;
    if (showCompanyPicker && !form.companyId.trim()) {
      toast.error("Select a company.");
      return;
    }
    if (!form.biometricDeviceId.trim()) {
      toast.error("Select a device type from the catalog.");
      return;
    }
    if (!form.ipAddress.trim()) {
      toast.error("IP address is required.");
      return;
    }

    setIsSaving(true);
    try {
      const r = await updateCompanyBiometricDevice(id, companyBiometricDeviceFormToPayload(form));
      toast.success(r.message);
      navigate(`/company-biometric-devices/${id}`);
    } catch (e) {
      console.error("[EditCompanyBiometricDevice] Save failed", e);
      toast.error(companyBiometricDeviceFormErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  if (!canUpdate) {
    return (
      <PermissionDenied
        title="Cannot edit company biometric devices"
        message="You don’t have permission to update company biometric devices. Ask an administrator to grant company_biometric_device_can_update."
      />
    );
  }

  if (!id) {
    return (
      <Layout title="Edit company biometric device">
        <div className="glass-card p-8 text-sm text-muted-foreground">Missing device id.</div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Edit company biometric device">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading device…
        </div>
      </Layout>
    );
  }

  if (notFound || !form) {
    return (
      <Layout title="Edit company biometric device">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Company biometric device not found.</div>
          <Button variant="glass" className="gap-2" asChild>
            <Link to="/company-biometric-devices">
              <ArrowLeft className="w-4 h-4" />
              Back to devices
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`Edit: ${form.ipAddress}`}>
      <div className="w-full max-w-none space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Button type="button" variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5" onClick={handleCancel}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground truncate">Edit company biometric device</h1>
              <p className="text-sm text-muted-foreground mt-1 font-mono truncate">{form.ipAddress}:{form.port}</p>
            </div>
          </div>
          <Button variant="premium" className="gap-2 shrink-0" disabled={isSaving} onClick={() => void save()}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save changes
          </Button>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Fingerprint className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-foreground text-sm">Company hardware</h2>
              <p className="text-xs text-muted-foreground">Update network settings and active status.</p>
            </div>
          </div>
          <CompanyBiometricDeviceUpsertFormFields
            form={form}
            onChange={(patch) => setForm((f) => (f ? { ...f, ...patch } : f))}
            showCompanyPicker={showCompanyPicker}
          />
        </motion.div>
      </div>
    </Layout>
  );
};

export default EditCompanyBiometricDevice;
